// src/api/wsClient.ts
// wsClient — WebSocket transport layer with reconnect + frame parsing.
//
// Responsibilities:
//   - Establish WS connection with exponential backoff
//   - Parse raw WS messages into Frame objects
//   - Write frames to FrameCache
//   - Emit connection status events
//
// Does NOT do: caching, state, diff, UI logic

import type { UIFrame } from '../core/ui_contract';
import type { FrameCache } from '../core/frame_cache';
import { EventEmitter } from 'eventemitter3';

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'reconnecting';

// Raw WS message — backend sends stream_message wrapper
export interface WSMessage {
  type: 'stream_message';
  stream: string;
  seq: number;
  ts: string;
  data: {
    type: 'uiframe' | 'uiframe_sync' | 'pong' | 'subscribed' | 'unsubscribed' | 'error';
    frame?: UIFrame;
    message?: string;
  };
}

interface WsClientConfig {
  url: string;
  frameCache: FrameCache;
  reconnectBaseDelayMs?: number;
  reconnectMaxDelayMs?: number;
  heartbeatIntervalMs?: number;
}

const DEFAULT_CONFIG = {
  reconnectBaseDelayMs: 1000,
  reconnectMaxDelayMs: 30000,
  heartbeatIntervalMs: 30000,
};

export class WSClient extends EventEmitter {
  private ws: WebSocket | null = null;
  private config: Required<WsClientConfig>;
  private frameCache: FrameCache;
  private status: ConnectionStatus = 'disconnected';
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionalClose = false;

  constructor(config: WsClientConfig) {
    super();
    this.config = { ...DEFAULT_CONFIG, ...config } as Required<WsClientConfig>;
    this.frameCache = config.frameCache;
  }

  // ---- Public API ----

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) return;
    this.intentionalClose = false;
    this.setStatus('connecting');
    this.clearTimers();

    const wsUrl = this.config.url;

    try {
      this.ws = new WebSocket(wsUrl);
      this.ws.onopen = this.onOpen.bind(this);
      this.ws.onmessage = this.onMessage.bind(this);
      this.ws.onclose = this.onClose.bind(this);
      this.ws.onerror = this.onError.bind(this);
    } catch (err) {
      console.error('[wsClient] Failed to create WebSocket:', err);
      this.scheduleReconnect();
    }
  }

  disconnect(): void {
    this.intentionalClose = true;
    this.clearTimers();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.setStatus('disconnected');
  }

  getStatus(): ConnectionStatus {
    return this.status;
  }

  // ---- Connection Lifecycle ----

  private onOpen(): void {
    console.log('[wsClient] Connected');
    this.reconnectAttempt = 0;
    this.setStatus('connected');
    this.startHeartbeat();
    this.emit('connected');
  }

  private onClose(event: CloseEvent): void {
    console.log('[wsClient] Connection closed:', event.code, event.reason);
    this.clearTimers();
    this.ws = null;

    if (!this.intentionalClose) {
      this.setStatus('reconnecting');
      this.scheduleReconnect();
    }
  }

  private onError(event: Event): void {
    console.error('[wsClient] Error:', event);
    this.emit('error', event);
  }

  // ---- Reconnect with Exponential Backoff ----

  private scheduleReconnect(): void {
    if (this.intentionalClose) return;

    const delay = Math.min(
      this.config.reconnectBaseDelayMs * Math.pow(2, this.reconnectAttempt),
      this.config.reconnectMaxDelayMs
    );

    console.log(`[wsClient] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempt + 1})`);
    this.setStatus('reconnecting');

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempt++;
      this.connect();
    }, delay);
  }

  // ---- Heartbeat ----

  private startHeartbeat(): void {
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, this.config.heartbeatIntervalMs);
  }

  // ---- Message Parsing ----

  private onMessage(event: MessageEvent): void {
    try {
      const raw = JSON.parse(event.data as string);
      if (raw.type === 'stream_message' && raw.stream === 'ui') {
        this.handleStreamMessage(raw as WSMessage);
      } else if (raw.type === 'stream_message' && raw.stream === 'ranking') {
        this.handleRankingMessage(raw);
      }
    } catch (err) {
      console.error('[wsClient] Failed to parse message:', err);
    }
  }

  private handleRankingMessage(msg: any): void {
    const { data } = msg;
    if (data.type === 'rank.changed') {
      this.emit('rank_changed', data);
    }
  }

  private handleStreamMessage(msg: WSMessage): void {
    const { stream, seq, data } = msg;

    if (stream !== 'ui') return;

    if (data.type === 'uiframe' && data.frame) {
      this.handleUIFrame(seq, data.frame);
    } else if (data.type === 'uiframe_sync' && data.frame) {
      this.handleUIFrame(seq, data.frame, true);
    }
  }

  private handleUIFrame(seq: number, frameData: UIFrame, isSync = false): void {
    const { generated_at_ms, frame_checksum } = frameData;
    const frame = this.frameCache.push(seq, generated_at_ms, frame_checksum, frameData);
    this.emit('frame', frame, { isSync });
  }

  // ---- Status Helpers ----

  private setStatus(status: ConnectionStatus): void {
    if (this.status !== status) {
      this.status = status;
      this.emit('status', status);
    }
  }

  private clearTimers(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.reconnectTimer = null;
    this.heartbeatTimer = null;
  }
}
